/*
 * Lumidolce theme — vanilla port of the Next.js client components:
 * BuyBox (→ Shopify cart → checkout), Countdown, MobileNav and StickyCta.
 * Markup and classes mirror src/components/*.tsx exactly.
 */
(() => {
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const json = (id) => {
    try {
      return JSON.parse(document.getElementById(id).textContent);
    } catch {
      return null;
    }
  };
  const esc = (s) =>
    String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
  const t = (s, v = {}) => s.replace(/\{(\w+)\}/g, (m, k) => (k in v ? String(v[k]) : m));

  const C = json("lumi-config");

  /* ---------------- Mobile nav (all pages) ---------------- */
  const menuBtn = $('[aria-controls="mobile-nav"]');
  if (menuBtn) {
    const menuLabel = menuBtn.getAttribute("aria-label");
    const closeLabel = C?.nav.close ?? (document.documentElement.lang.startsWith("es") ? "Cerrar menú" : "Close menu");
    const items = $$('header nav[aria-label="Main"] a').map((a) => ({ href: a.getAttribute("href"), label: a.textContent }));
    const shopLink = $$("header a").find((a) => /#shop$/.test(a.getAttribute("href") || "") && a.classList.contains("bg-accent"));
    const path = menuBtn.querySelector("path");
    let nav = null;

    const setOpen = (open) => {
      menuBtn.setAttribute("aria-expanded", String(open));
      menuBtn.setAttribute("aria-label", open ? closeLabel : menuLabel);
      path.setAttribute("d", open ? "m5 5 10 10M15 5 5 15" : "M3.5 7h13M3.5 13h13");
      document.body.style.overflow = open ? "hidden" : "";
      if (open && !nav) {
        nav = document.createElement("nav");
        nav.id = "mobile-nav";
        nav.className = "absolute inset-x-0 top-full z-40 h-dvh bg-paper px-6 pt-2";
        nav.innerHTML =
          `<ul class="flex flex-col">${items
            .map((i) => `<li class="border-b border-line"><a href="${esc(i.href)}" class="block py-5 font-serif text-[30px]">${esc(i.label)}</a></li>`)
            .join("")}</ul>` +
          (shopLink
            ? `<a href="${esc(shopLink.getAttribute("href"))}" class="mt-8 block rounded-full bg-accent px-6 py-4 text-center text-[16px] font-semibold text-white">${esc(shopLink.textContent)}</a>`
            : "");
        nav.addEventListener("click", (e) => e.target.closest("a") && setOpen(false));
        menuBtn.parentElement.appendChild(nav);
      } else if (!open && nav) {
        nav.remove();
        nav = null;
      }
    };
    menuBtn.addEventListener("click", () => setOpen(menuBtn.getAttribute("aria-expanded") !== "true"));
  }

  /* ---------------- Countdown (header bar on every page + buy box) ---------------- */
  const endsAt = $("[data-ends-at]")?.dataset.endsAt;
  if (endsAt) {
    const end = Math.floor(new Date(endsAt).getTime() / 1000);
    const pad = (n) => String(n).padStart(2, "0");
    const tick = () => {
      const left = end - Math.floor(Date.now() / 1000);
      const timers = $$('[role="timer"]');
      if (left <= 0) {
        timers.forEach((el) => el.parentElement.remove());
        clearInterval(id);
        return;
      }
      const parts = [Math.floor(left / 86400), Math.floor((left % 86400) / 3600), Math.floor((left % 3600) / 60), left % 60];
      for (const el of timers) {
        if (el.closest("#lumi-buy")) {
          [...el.children].forEach((span, i) => (span.firstChild.nodeValue = pad(parts[i])));
        } else {
          const labels = (el.dataset.units || "d,h,m,s").split(",");
          el.textContent = parts.map((n, i) => pad(n) + labels[i]).join(" ");
        }
      }
    };
    const id = setInterval(tick, 1000);
    tick();
  }

  if (!C) return; // Everything below is the landing page only.

  const fmt = (n) =>
    new Intl.NumberFormat(C.numberLocale, {
      style: "currency",
      currency: C.currency,
      minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(n);

  /* ---------------- Buy box ---------------- */
  const form = $("#lumi-buy");
  const P = json("lumi-product") || { found: false };

  // Map Shopify variants to our colorways by option value (English or Spanish names).
  const aliases = {
    cherry: ["cherry", "cereza", "rojo", "red", "granate"],
    snow: ["snow", "nieve", "blanco", "white"],
    cocoa: ["cocoa", "cacao", "chocolate", "marrón", "marron", "brown", "negro", "black"],
  };
  const colorOf = (value) => {
    const v = String(value).trim().toLowerCase();
    return C.colors.find((c) => aliases[c].includes(v) || v === C.colorMeta[c].shopifyValue.toLowerCase());
  };
  const variants = {}; // color -> variant
  const stock = {}; // color -> units left (0 = sold out), like getStock()
  let fallbackVariant = null; // product without color variants: color goes as a line-item property
  if (P.found) {
    const colorIdx = (P.options || []).findIndex((o) => /colou?r/i.test(o));
    for (const v of P.variants) {
      const opts = colorIdx >= 0 ? [v.options[colorIdx]] : v.options;
      const color = opts.map(colorOf).find(Boolean);
      if (!color) continue;
      variants[color] = v;
      if (!v.available) stock[color] = 0;
      else if (v.qty != null && v.qty > 0) stock[color] = v.qty;
    }
    if (!Object.keys(variants).length) fallbackVariant = P.variants.find((v) => v.available) || P.variants[0];
  }

  if (form) {
    const D = C.buy;
    const tierOf = (id) => C.tiers.find((x) => x.id === id);
    const available = (c) => (stock[c] === 0 ? (C.colors.find((x) => stock[x] !== 0) ?? c) : c);
    let tierId = C.defaultTierId;
    let picked = tierOf(tierId).defaultColors.map(available);
    let pending = false;

    const fieldsets = $$("fieldset", form);
    const tierLabels = $$("label", fieldsets[0]);
    const colorFs = fieldsets[1];
    const legend = colorFs.querySelector("legend").outerHTML;
    const totalPrices = $("div.items-end > span:last-child", form);
    const submit = $('button[type="submit"]', form);
    const submitHtml = submit.innerHTML;
    const labelBase =
      "relative flex cursor-pointer items-center gap-4 rounded-2xl border-2 px-4 py-4 transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent has-[:focus-visible]:ring-offset-2 ";

    const renderTiers = () => {
      tierLabels.forEach((label) => {
        const radio = label.querySelector("input");
        const sel = radio.value === tierId;
        radio.checked = sel;
        label.className = labelBase + (sel ? "border-accent bg-accent-soft/60" : "border-line hover:border-ink-soft");
        const dot = radio.nextElementSibling;
        dot.className = "grid size-5 shrink-0 place-items-center rounded-full border-2 " + (sel ? "border-accent" : "border-muted");
        dot.innerHTML = sel ? '<span class="size-2.5 rounded-full bg-accent"></span>' : "";
      });
    };

    const renderColors = (focus) => {
      const tier = tierOf(tierId);
      const names = C.colorNames;
      const rows = picked.slice(0, tier.quantity).map((current, i) => {
        const mug = t(D.mugN, { n: i + 1 });
        const label =
          tier.quantity > 1 ? `${esc(mug)}<span class="ml-2 font-medium text-ink">${esc(names[current])}</span>` : esc(names[current]);
        const buttons = C.colors
          .map((c) => {
            const on = c === current;
            const soldOut = stock[c] === 0;
            const title = soldOut ? t(C.stock.soldOut, { color: names[c] }) : names[c];
            return `<button type="button" role="radio" aria-checked="${on}" aria-label="${esc(title)}" title="${esc(title)}"${soldOut ? " disabled" : ""} data-row="${i}" data-color="${c}" class="relative grid size-9 place-items-center rounded-full border-2 transition-all disabled:cursor-not-allowed disabled:opacity-35 ${on ? "border-ink" : "border-transparent hover:border-line"}"><span class="size-6 rounded-full border border-black/10" style="background:${C.colorMeta[c].swatch}"></span>${soldOut ? '<span aria-hidden="true" class="absolute h-[2px] w-8 rotate-45 bg-ink"></span>' : ""}</button>`;
          })
          .join("");
        return `<div class="flex items-center justify-between gap-3"><span class="text-[14px] text-ink-soft">${label}</span><div role="radiogroup" aria-label="${esc(mug)}" class="flex gap-2">${buttons}</div></div>`;
      });
      const low = [...new Set(picked.slice(0, tier.quantity))]
        .map((c) => {
          const left = stock[c];
          if (left === undefined || left === 0 || left > C.lowStockThreshold) return "";
          return `<p class="flex items-center gap-2 text-[13px] font-semibold text-accent"><span class="relative flex size-2"><span class="absolute inline-flex size-full animate-ping rounded-full bg-accent opacity-60 motion-reduce:animate-none"></span><span class="relative inline-flex size-2 rounded-full bg-accent"></span></span>${esc(t(C.stock.low, { n: left, color: C.colorNames[c] }))}</p>`;
        })
        .join("");
      colorFs.innerHTML = legend + rows.join("") + low;
      if (focus) colorFs.querySelector(`[data-row="${focus.row}"][data-color="${focus.color}"]`)?.focus();
    };

    const renderTotal = () => {
      const tier = tierOf(tierId);
      const regular = tier.quantity * C.unitPrice;
      totalPrices.innerHTML =
        (regular - tier.price > 0 ? `<span class="text-[16px] text-muted line-through">${esc(fmt(regular))}</span>` : "") +
        `<span class="font-serif text-[40px] leading-none">${esc(fmt(tier.price))}</span>`;
    };

    const showError = (key) => {
      $('[role="alert"]', form)?.remove();
      if (!key) return;
      submit.insertAdjacentHTML(
        "afterend",
        `<p role="alert" class="rounded-xl border border-accent/30 bg-accent-soft px-4 py-3 text-[14px] text-accent">${esc(t(D.errors[key], { email: C.email }))}</p>`,
      );
    };

    const setPending = (on) => {
      pending = on;
      submit.disabled = on;
      submit.innerHTML = on ? esc(D.pending) : submitHtml;
    };

    fieldsets[0].addEventListener("change", (e) => {
      if (e.target.name !== "tier-choice") return;
      const next = tierOf(e.target.value);
      tierId = next.id;
      picked = next.defaultColors.map((c, i) => picked[i] ?? available(c));
      renderTiers();
      renderColors();
      renderTotal();
    });

    colorFs.addEventListener("click", (e) => {
      const btn = e.target.closest("button[data-color]");
      if (!btn || btn.disabled) return;
      const row = Number(btn.dataset.row);
      picked = picked.map((p, j) => (j === row ? btn.dataset.color : p));
      renderColors({ row, color: btn.dataset.color });
    });

    const post = async (url, body) => {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!res.ok) {
        const err = new Error(`${url} ${res.status}`);
        err.status = res.status;
        throw err;
      }
      return res.json();
    };

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      if (pending) return;
      showError(null);
      const tier = tierOf(tierId);
      const chosen = picked.slice(0, tier.quantity);
      if (!P.found || (!fallbackVariant && !Object.keys(variants).length)) return showError("notConnected");

      const lines = new Map();
      for (const color of chosen) {
        const variant = variants[color] || fallbackVariant;
        if (!variant?.available) return showError("unavailable");
        const key = fallbackVariant ? `${variant.id}:${color}` : String(variant.id);
        const line = lines.get(key) || {
          id: variant.id,
          quantity: 0,
          ...(fallbackVariant && { properties: { Color: C.colorMeta[color].shopifyValue } }),
        };
        line.quantity += 1;
        lines.set(key, line);
      }

      setPending(true);
      try {
        const root = window.Shopify?.routes?.root || "/";
        await post(root + "cart/clear.js");
        await post(root + "cart/add.js", { items: [...lines.values()] });
        await post(root + "cart/update.js", { attributes: { bundle: tier.id } });
        const params = new URLSearchParams({ locale: C.locale });
        if (tier.discountCode) params.set("discount", tier.discountCode);
        location.href = `${root}checkout?${params}`;
      } catch (error) {
        console.error("[checkout]", error);
        setPending(false);
        showError(error.status === 422 ? "unavailable" : "generic");
      }
    });

    // Coming back from checkout with the back button: re-enable the button.
    addEventListener("pageshow", (e) => e.persisted && setPending(false));

    renderTiers();
    renderColors();
    renderTotal();
  }

  /* ---------------- Sticky CTA (mobile) ---------------- */
  const sticky = $("#lumi-sticky");
  const hero = $("#hero-cta");
  const shop = $("#shop");
  if (sticky && hero && shop && "IntersectionObserver" in window) {
    const link = sticky.querySelector("a");
    const seen = new Map();
    const observer = new IntersectionObserver((entries) => {
      for (const e of entries) seen.set(e.target, e.isIntersecting);
      const visible = !seen.get(hero) && hero.getBoundingClientRect().bottom < 0 && !seen.get(shop);
      sticky.setAttribute("aria-hidden", String(!visible));
      sticky.classList.toggle("translate-y-0", visible);
      sticky.classList.toggle("translate-y-full", !visible);
      link.tabIndex = visible ? 0 : -1;
    });
    observer.observe(hero);
    observer.observe(shop);
  }
})();
